// 使用方式
//
// 1. 基础 Buy Now（variant 来源优先级：form input[name="id"] > button/元素 data-variant-id）
// <buy-now-button>
//   <button class="ug-buyNow-bth" data-variant-id="1234567890">
//     <span class="button-text">Buy Now</span>
//   </button>
// </buy-now-button>
//
// 2. 带数量（button 加 data-quantity-control，组件内或页面找 .quantity__input）
// <buy-now-button>
//   <input class="quantity__input" type="number" value="1" min="1">
//   <button class="ug-buyNow-bth" data-variant-id="1234567890" data-quantity-control>
//     <span class="button-text">Buy Now</span>
//   </button>
// </buy-now-button>
//
// 3. 订单行备注 properties（form 优先，同 key 以 form 为准；无 form 时用 data-note-properties）
//
// 方式 A — form 内 hidden input（推荐，如延保页）：
// <form class="ug-checkout-addtocart-form">
//   <input type="hidden" name="id" value="1234567890">
//   <input type="hidden" name="properties[SN]" value="ABC123456789">
//   <input type="hidden" name="properties[Model]" value="DXP4800">
//   <buy-now-button>
//     <button class="ug-buyNow-bth"><span class="button-text">Checkout</span></button>
//   </buy-now-button>
// </form>
//
// 方式 B — button 上 data-note-properties 与 data-variant-id 同级（非法 JSON 会 console.warn 并忽略）：
// Liquid: data-note-properties='{{ props | json | escape }}'
// <buy-now-button>
//   <button
//     class="ug-buyNow-bth"
//     data-variant-id="1234567890"
//     data-note-properties='{"SN":"ABC123456789","Model":"DXP4800"}'
//   >
//     <span class="button-text">Buy Now</span>
//   </button>
// </buy-now-button>
//
// form 查找范围：最近 product-form，或 form.ug-checkout-addtocart-form

class BuyNowButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('.ug-buyNow-bth');
    this.quantityInput =
      this.querySelector('[data-quantity-value]') ||
      this.closest('.sticky-bottom-bar__form')?.querySelector('.quantity__input') ||
      document.querySelector('.sticky-bottom-bar__form .quantity__input') ||
      document.querySelector('.product-form__quantity .quantity__input');
    this.init();
  }

  init() {
    if (this.button) {
      this.button.addEventListener('click', this.handleClick.bind(this));
    }
  }

  getCheckoutForm() {
    return this.closest('product-form') || this.closest('form.ug-checkout-addtocart-form');
  }

  parseNotePropertiesAttr() {
    const raw =
      this.button?.getAttribute('data-note-properties') ||
      this.getAttribute('data-note-properties');
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      console.warn('[buy-now-button] data-note-properties must be a JSON object');
    } catch (error) {
      console.warn('[buy-now-button] Invalid data-note-properties JSON:', error);
    }

    return {};
  }

  getPropertiesFromForm() {
    const form = this.getCheckoutForm();
    if (!form) return {};

    const properties = {};
    form.querySelectorAll('[name^="properties["]').forEach((input) => {
      const name = input.getAttribute('name');
      const match = name?.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = input.value;
    });
    return properties;
  }

  getNoteProperties() {
    return { ...this.parseNotePropertiesAttr(), ...this.getPropertiesFromForm() };
  }

  getProductData() {
    const variantAttr = this.button?.getAttribute('data-variant-id') || this.getAttribute('data-variant-id');
    const variantId = this.getCheckoutForm()?.querySelector('input[name="id"]')?.value || variantAttr;
    return {
      variantId,
      noteProperties: this.getNoteProperties(),
      productHandle: this.button?.getAttribute('data-product-handle') || this.getAttribute('data-product-handle'),
      productId: this.button?.getAttribute('data-product-id') || this.getAttribute('data-product-id')
    };
  }


  setLoadingState(button, isLoading) {
    const spinner = button.querySelector('.loading__spinner');
    const buttonText = button.querySelector('.button-text');

    if (isLoading) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', true);
      spinner?.classList.remove('hidden');
      buttonText?.classList.add('hidden');
    } else {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
      buttonText?.classList.remove('hidden');
    }
  }

  async clearCart() {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to clear cart');
    }

    return response;
  }

  async addToCart(variantId, quantity = 1, properties = {}) {
    const item = { id: variantId, quantity };
    if (properties && Object.keys(properties).length > 0) {
      item.properties = properties;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [item],
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return response;
  }

  async applyGloodUpsell(variantId) {
    if (!window.Glood?.upsell) return;

    await Glood.upsell.commitPickSelections({
      triggerVariantId: variantId,
    });
    await Glood.upsell.addQualifyingFreeGifts();
  }

  async processCheckout(button, variantId, noteProperties = {}) {
    try {
      this.setLoadingState(button, true);
      // 获取数量
      const useQuantityInput = button.hasAttribute('data-quantity-control');
      const quantity = useQuantityInput
        ? parseInt((this.quantityInput && this.quantityInput.value) ? this.quantityInput.value : 1, 10)
        : 1;

      await this.clearCart();
      await this.addToCart(variantId, quantity, noteProperties);
      await this.applyGloodUpsell(variantId);

      this.redirectToCheckout();

    } catch (error) {
      console.error('Checkout error:', error);
      this.handleError('There was an error processing your request. Please try again.');
    } finally {
      this.setLoadingState(button, false);
    }
  }

  async handleClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const productData = this.getProductData();
    
    console.log('Checkout attempt with:', productData);

    const checkoutVariantId = productData.variantId;

    if (checkoutVariantId) {
      await this.processCheckout(button, checkoutVariantId, productData.noteProperties);
    } else if (productData.productHandle) {
      this.redirectToProduct(productData.productHandle);
    } else {
      this.handleError("Sorry, we couldn't find the product information. Please try another product.");
    }
  }

  redirectToCheckout() {
    window.location.href = '/checkout';
  }

  redirectToProduct(productHandle) {
    window.location.href = '/products/' + productHandle;
  }

  handleError(message) {
    console.error(message);
    alert(message);
  }

}

customElements.define('buy-now-button', BuyNowButton);
